import { Box, Typography, Stack } from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import QuestionItem from './QuestionItem';
import { SurveyQuestion } from '../../types/survey';

interface QuestionListProps {
  questions: SurveyQuestion[];
  onEditQuestion: (question: SurveyQuestion) => void;
  onDeleteQuestion: (questionId: string) => void;
  onReorderQuestions: (reorderedQuestions: SurveyQuestion[]) => void;
}

const QuestionList = ({ questions, onEditQuestion, onDeleteQuestion, onReorderQuestions }: QuestionListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = questions.findIndex((q, idx) => (q.id || `question-${idx}`) === active.id);
      const newIndex = questions.findIndex((q, idx) => (q.id || `question-${idx}`) === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedQuestions = arrayMove(questions, oldIndex, newIndex);
        onReorderQuestions(reorderedQuestions);
      }
    }
  };

  if (questions.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          fontSize: '1.5rem',
          mb: 2,
          color: 'text.primary'
        }}
      >
        Survey Questions ({questions.length})
      </Typography>
      
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={questions.map((q, idx) => q.id || `question-${idx}`)}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={2}>
            {questions.map((question, index) => (
              <QuestionItem
                key={question.id}
                question={question}
                index={index}
                totalQuestions={questions.length}
                onEdit={onEditQuestion}
                onDelete={onDeleteQuestion}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default QuestionList;

